import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const AsyncDomainSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [domains, setDomains] = useState<string[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);

  const fetchDomains = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/domain`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch domains: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const domainList = await response.json();
      setDomains(Array.isArray(domainList) ? domainList : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch domains.');
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newDomain = event.target.value as string;
    setSelectedDomain(newDomain);
    onChange?.(newDomain);
  };

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth>
      <InputLabel id="domain-select-label">Domain</InputLabel>
      <Select labelId="domain-select-label" value={selectedDomain} onChange={handleChange}>
        {domains.map((domain) => (
          <MenuItem key={domain} value={domain}>
            {domain}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && <FormHelperText>Select a domain from the list.</FormHelperText>}
    </FormControl>
  );
};